import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReplaySubject } from 'rxjs';

import * as Actions from '../../store/csw/csw.actions';
import { InsuranceProviders } from '../../enums/insurance-providers';
import Global from '../../store/global-selectors';
import { mapServiceMappingPackageToFulfillmentPlanPackage } from '../../store/map-requested-service/map-requested-service.observables';
import { PackageService, isGenericPackage } from '../../services/package-service';
import { USD } from '../../constants/currency-codes';
import { getPackageIds } from '../package/dropdown/package-id-helpers';
import { setupV2PackageSet, setupV3PackageSet } from '../../components/modals/multi-package-modal/setup-package-set';
import { parsePackageId } from '@shipstation/client-common/utils/package-utils';
import { hasAnyDimensionValues } from '@shipstation/client-common/utils/dimensions';
import { useEnableCarrierPackageDimensionLock } from '../../feature-flags/hooks/use-enable-carrier-package-dimension-lock';

export const readWeightFromScaleHotkeySubject = new ReplaySubject(1, 5000);
export const returnWeightItemPickerSubject = new ReplaySubject(1, 5000);

export const usePackagesState = (fulfillmentPlanId, packages, onConfigurationChange, sellerDefaultDimensionsUnit) => {
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || USD;
  const isCarrierPackageDimensionLockEnabled = useEnableCarrierPackageDimensionLock();

  const dispatch = useDispatch();
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const salesOrdersFulfilledIds = useMemo(() => fulfillmentPlan?.salesOrdersFulfilledIds || [], [fulfillmentPlan?.salesOrdersFulfilledIds]);
  const salesOrder = useSelector((state) =>
    Global.selectEntitiesSalesOrderById(state, {
      salesOrderId: salesOrdersFulfilledIds[0],
    }),
  );

  const onPackageChange = useCallback(
    (packageId, dimensions) => {
      const { packageTypeId: parsedPackageTypeId, isCustom, carrierId } = parsePackageId(packageId);

      const shouldApplyDimensions = isCustom || (isCarrierPackageDimensionLockEnabled && carrierId && hasAnyDimensionValues(dimensions));
      const realDimensions = shouldApplyDimensions ? { ...dimensions } : {};

      const [firstPackage = {}] = packages;
      const newPackageTypeId = parsedPackageTypeId ?? firstPackage.packageTypeId;
      const newPackageId = packageId || firstPackage.packageId;
      const updatedPackage = {
        ...firstPackage,
        packageTypeId: newPackageTypeId,
        packageId: newPackageId,
        dimensions: {
          ...firstPackage.dimensions,
          ...realDimensions,
        },
      };
      dispatch(
        Actions.updateShipmentConfigurationPackages(fulfillmentPlanId, {
          packageId,
          dimensions: realDimensions,
        }),
      );
      onConfigurationChange({ packages: [updatedPackage] });
    },
    [dispatch, fulfillmentPlanId, packages, onConfigurationChange, isCarrierPackageDimensionLockEnabled],
  );

  const onPackagesSave = useCallback(
    (newPackages) => {
      dispatch(
        Actions.updateShipmentConfiguration(fulfillmentPlanId, {
          packages: newPackages,
        }),
      );
      onConfigurationChange({ packages: newPackages });
    },
    [dispatch, fulfillmentPlanId, onConfigurationChange],
  );

  const onWeightAndDimsChange = useCallback(
    (updatedWeight, updatedDimensions) => {
      const [firstPackage] = packages;
      const reduxUpdate = {};
      const updatedPackage = {
        ...firstPackage,
      };
      if (updatedWeight) {
        updatedPackage.weight = {
          ...updatedPackage.weight,
          ...updatedWeight,
        };
        reduxUpdate.weight = updatedWeight;
      }
      if (updatedDimensions) {
        updatedPackage.dimensions = {
          ...updatedPackage.dimensions,
          ...updatedDimensions,
        };
        reduxUpdate.dimensions = updatedDimensions;
      }

      dispatch(Actions.updateShipmentConfigurationPackages(fulfillmentPlanId, reduxUpdate));
      onConfigurationChange({ packages: [updatedPackage] });
    },
    [dispatch, fulfillmentPlanId, packages, onConfigurationChange],
  );

  const onContentDescriptionChange = useCallback(
    (updatedContentDescription) => {
      const [firstPackage, ...otherPackages] = packages;
      const updatedPackage = {
        ...firstPackage,
        contentDescription: updatedContentDescription,
      };
      const updatedPackages = [updatedPackage, ...otherPackages];
      dispatch(
        Actions.updateShipmentConfigurationPackages(fulfillmentPlanId, {
          contentDescription: updatedContentDescription,
        }),
      );
      onConfigurationChange({ packages: updatedPackages });
    },
    [dispatch, packages, fulfillmentPlanId, onConfigurationChange],
  );

  const onInsuredAmountChange = useCallback(
    (insuredAmount, currencyCode) => {
      const parsedValue = Number.parseFloat(insuredAmount);
      const insuredValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
      const [firstPackage] = packages;
      const updatedPackage = {
        ...firstPackage,
        insuredValue: {
          value: insuredValue,
          code: currencyCode,
        },
      };
      dispatch(
        Actions.updateShipmentConfigurationPackages(fulfillmentPlanId, {
          insuredValue: { value: insuredValue, code: currencyCode },
        }),
      );
      onConfigurationChange({ packages: [updatedPackage] });
    },
    [dispatch, fulfillmentPlanId, packages, onConfigurationChange],
  );

  const clearInsuranceValues = useCallback(() => {
    const newPackages = packages.map((p) => {
      return {
        ...p,
        insuredValue: {
          ...p.insuredValue,
          value: 0,
        },
      };
    });
    onConfigurationChange({ packages: newPackages });
  }, [packages, onConfigurationChange]);

  const onMultiPackageSave = useCallback(
    (multiPackages) => {
      dispatch(Actions.saveMultiPackageSet(fulfillmentPlanId, multiPackages));
      onConfigurationChange({ packages: multiPackages });
    },
    [dispatch, fulfillmentPlanId, onConfigurationChange],
  );

  const applyPackageSetFromList = useCallback(
    (packageSet) => {
      const jsonData = packageSet?.jsonData;
      const parsedData = jsonData ? JSON.parse(jsonData) : {};
      let packagesWithUnits;
      if (Array.isArray(parsedData)) {
        packagesWithUnits = setupV2PackageSet(parsedData, sellerDefaultDimensionsUnit);
      } else {
        packagesWithUnits = setupV3PackageSet(parsedData, sellerDefaultDimensionsUnit);
      }
      onMultiPackageSave(packagesWithUnits);
    },
    [onMultiPackageSave, sellerDefaultDimensionsUnit],
  );

  const sellerPackages = useSelector(Global.selectSellerPackages);
  const onApplyServiceMappingPackage = useCallback(
    (packageTypeId) => {
      const serviceMappingPackage = sellerPackages.find((pkg) => pkg.packageTypeId === packageTypeId);
      if (serviceMappingPackage) {
        const newPackage = mapServiceMappingPackageToFulfillmentPlanPackage(serviceMappingPackage, fulfillmentPlan);
        onConfigurationChange({ packages: [newPackage] });
      }
    },
    [fulfillmentPlan, sellerPackages, onConfigurationChange],
  );

  const selectShouldAutoFillInsureAmount = useCallback(() => {
    if (packages.length > 1) return false;
    const fulfillsSingleOrder = (salesOrdersFulfilledIds ?? []).length === 1;

    if (!fulfillsSingleOrder) return false;

    const orderHasSingleFulfillmentPlan = (salesOrder?.fulfillmentPlanIds ?? []).length === 1;

    return orderHasSingleFulfillmentPlan;
  }, [salesOrder, salesOrdersFulfilledIds, packages]);

  const calculateRecommendedInsuranceValue = useCallback(
    (newInsuranceProviderName) => {
      if (!selectShouldAutoFillInsureAmount()) return;

      const totalInsuredAmount = packages.reduce((sum, p) => {
        return sum + (p?.insuredValue?.value ?? 0);
      }, 0);

      if (totalInsuredAmount !== 0) {
        return totalInsuredAmount;
      }
      const { amountSummary } = salesOrder;

      if (amountSummary) {
        const { orderTotal, taxPaid } = amountSummary ?? {};
        const insuranceProvider = newInsuranceProviderName ?? fulfillmentPlan?.insuranceProvider?.name;
        if (insuranceProvider === InsuranceProviders.XCover.name && orderTotal && taxPaid) {
          return orderTotal.value - taxPaid.value;
        }
        return orderTotal.value;
      }
    },
    [fulfillmentPlan?.insuranceProvider?.name, packages, salesOrder, selectShouldAutoFillInsureAmount],
  );

  const initInsuranceValue = useCallback(
    (insuranceProviderName) => {
      if (!selectShouldAutoFillInsureAmount()) return;

      const insuranceAmount = calculateRecommendedInsuranceValue(insuranceProviderName);
      const currencyCode = insuranceProviderName === InsuranceProviders.Shipsurance.name ? USD : sellerCurrencyCode;

      if (insuranceAmount !== 0) {
        onInsuredAmountChange(insuranceAmount, currencyCode);
      }
    },
    [calculateRecommendedInsuranceValue, onInsuredAmountChange, selectShouldAutoFillInsureAmount, sellerCurrencyCode],
  );

  const sellerServices = useSelector(Global.selectSellerServices);
  const updatePackagesFromRateBrowserConfigureLabel = useCallback(
    ({ modelFromRateBrowser, rateBrowserSelection }) => {
      const { rate } = rateBrowserSelection;
      const { dimensions, weight } = modelFromRateBrowser;

      const { serviceId, packageTypeId } = rate;
      const sellerService = sellerServices.find((s) => s.serviceId === serviceId);
      const { packageId } = sellerPackages.find((p) => p.packageTypeId === packageTypeId) || {};
      const newPackage = {
        ...packages[0],
        packageId,
        dimensions,
        weight,
      };
      const newPackages = sellerService?.features?.supportsMultiPackage ? [newPackage, ...packages.slice(1)] : [newPackage];
      onPackagesSave(newPackages);
    },
    [onPackagesSave, packages, sellerPackages, sellerServices],
  );

  useEffect(() => {
    const subscription = readWeightFromScaleHotkeySubject.subscribe(
      ({ unit, value, dims, fulfillmentPlanId: scaleReadFulfillmentPlanId }) => {
        if (fulfillmentPlanId === scaleReadFulfillmentPlanId) {
          onWeightAndDimsChange({ unit, value }, dims);
        }
      },
    );

    return () => subscription.unsubscribe();
  }, [fulfillmentPlanId, onWeightAndDimsChange]);

  useEffect(() => {
    const subscription = returnWeightItemPickerSubject.subscribe(({ fulfillmentPlanId: _fulfillmentPlanId, weightValue }) => {
      if (fulfillmentPlanId === _fulfillmentPlanId) {
        onWeightAndDimsChange({ value: weightValue });
      }
    });

    return () => subscription.unsubscribe();
  }, [fulfillmentPlanId, onWeightAndDimsChange]);

  const checkPackagesAfterServiceChange = useCallback(
    (serviceId, serviceCollectionGuid) => {
      const sellerService = sellerServices.find((s) => s.serviceId === serviceId);
      const supportsMultiPackage = sellerService?.features?.supportsMultiPackage;
      if (packages.length > 1 && !supportsMultiPackage) {
        const combinedPackage = PackageService.combineMultiPackages(packages);
        onConfigurationChange({ packages: [combinedPackage] });
        return;
      }
      if (packages.length !== 1) {
        return;
      }

      const filterFunction = PackageService.getFilterFunction(sellerService);
      const filteredPackages = sellerPackages.filter(filterFunction);

      const packageIds = getPackageIds(packages, sellerServices, serviceId);
      const sanitizedPackageId = packageIds[0];

      const selectedPackage = filteredPackages.find((p) => p.packageId === sanitizedPackageId);
      const packageTypeIdIsNull = sanitizedPackageId?.split('-')[0] === 'null';

      const newServiceDoesNotSupportSelectedPackage =
        sanitizedPackageId && !selectedPackage && !packageTypeIdIsNull && !serviceCollectionGuid;
      if (newServiceDoesNotSupportSelectedPackage) {
        const newPackage = {
          ...packages[0],
          packageTypeId: null,
          packageId: undefined,
        };
        onConfigurationChange({ packages: [newPackage] });
        dispatch(
          Actions.updateShipmentConfigurationPackages(fulfillmentPlanId, {
            packageId: undefined,
            packageTypeId: null,
          }),
        );
      }

      const shouldSyncPackageIdForGenericPackage =
        isGenericPackage(sanitizedPackageId) &&
        selectedPackage &&
        !PackageService.compareCarrierIds(sanitizedPackageId, packages[0].packageId) &&
        !packageTypeIdIsNull;
      if (shouldSyncPackageIdForGenericPackage) {
        const newPackage = {
          ...packages[0],
          packageId: selectedPackage.packageId,
        };
        onConfigurationChange({ packages: [newPackage] });
        dispatch(
          Actions.updateShipmentConfigurationPackages(fulfillmentPlanId, {
            packageId: selectedPackage.packageId,
          }),
        );
      }
    },
    [dispatch, fulfillmentPlanId, sellerServices, packages, onConfigurationChange, sellerPackages],
  );

  return useMemo(
    () => ({
      checkPackagesAfterServiceChange,
      clearInsuranceValues,
      onApplyServiceMappingPackage,
      onContentDescriptionChange,
      onInsuredAmountChange,
      calculateRecommendedInsuranceValue,
      initInsuranceValue,
      onMultiPackageSave,
      onPackageChange,
      onPackagesSave,
      onWeightAndDimsChange,
      updatePackagesFromRateBrowserConfigureLabel,
      applyPackageSetFromList,
    }),
    [
      checkPackagesAfterServiceChange,
      clearInsuranceValues,
      calculateRecommendedInsuranceValue,
      initInsuranceValue,
      onApplyServiceMappingPackage,
      onContentDescriptionChange,
      onInsuredAmountChange,
      onMultiPackageSave,
      onPackageChange,
      onPackagesSave,
      onWeightAndDimsChange,
      updatePackagesFromRateBrowserConfigureLabel,
      applyPackageSetFromList,
    ],
  );
};
